import React, { useState } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
// Hooks
import useDialog from 'hooks/useDialog';
// Selectors
import { selectIsAuthenticated } from 'store/auth/authSelectors';
// Mui
import { makeStyles } from '@mui/styles';
import {
  AppBar, Toolbar, Box, Button, IconButton
} from '@mui/material';
import HouseOutlinedIcon from '@mui/icons-material/HouseOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import LoginIcon from '@mui/icons-material/Login';
// components
import SignInForm from 'components/SignIn.form';
import SignUpForm from 'components/SignUp.form';
import Menu from './Menu';
import UserMenu from './UserMenu';
// Utilities
import { getContent } from 'utilities/Utilities';

const Header:React.FC = () => {
  const classes = useStyles();
  const { pathname } = useLocation();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const handleOpenMenu = () => {
    setOpenMenu(!openMenu);
  }

  const { Dialog:SignInDialog, openDialog:openSignInDialog, closeDialog: closeSignInDialog } = useDialog();
  const { Dialog:SignUpDialog, openDialog:openSignUpDialog, closeDialog: closeSignUpDialog } = useDialog();

  const openSignIn = () => {
    closeSignUpDialog();
    openSignInDialog();
  }

  const openSignUp = () => {
    closeSignInDialog();
    openSignUpDialog();
  }

  return (
    <React.Fragment>
      <Menu open={openMenu} onClose={handleOpenMenu} />
      <SignInDialog maxWidth="sm">
        <SignInForm onClose={closeSignInDialog} openSignUp={openSignUp} />
      </SignInDialog>
      <SignUpDialog maxWidth="sm">
        <SignUpForm onClose={closeSignUpDialog} openSignIn={openSignIn} />
      </SignUpDialog>
      <AppBar position="relative" elevation={0} sx={{ background: '#fff', boxShadow: '0px 3px 5px -1px rgb(0 0 0 / 20%)' }}>
        <div className={pathname === '/' || pathname === '/timeshares' ? 'big-container' : 'container'}>
          <Toolbar disableGutters className={classes.toolbar}>
            <Box sx={{ display: 'flex',  alignItems: 'center', gap: '10px' }}>
              <IconButton
                onClick={handleOpenMenu}
                size="small"
                sx={{ '@media (min-width: 1240px)': { display: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <NavLink to="/">
                <img src={getContent('common').logoSrc} alt={getContent('common').name} className={classes.logo} />
              </NavLink>
              <Box sx={{ display: 'none',  alignItems: 'center', gap: '10px', '@media (min-width: 1240px)': { display: 'flex' } }}>
                <NavLink
                  className={classNames(classes.link, {
                    [classes.activeLink]: pathname === '/about-us',
                  })}
                  to="/about-us"
                >
                  About
                </NavLink>
                <NavLink
                  className={classNames(classes.link, {
                    [classes.activeLink]: pathname === '/contact-us',
                  })}
                  to="/contact-us"
                >
                  Contact us
                </NavLink>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isAuthenticated ? (
                  <UserMenu />
                ) : (
                  <React.Fragment>
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
                      {pathname !== '/list-your-property' && (
                        <React.Fragment>
                          <Button
                            variant="outlined"
                            component={NavLink}
                            to="list-your-property"
                            size="small"
                          >
                            {getContent('common').listYourProperty}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={openSignUpDialog}
                          >
                            Sign up
                          </Button>
                        </React.Fragment>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        onClick={openSignInDialog}
                      >
                        Sign in
                      </Button>
                    </Box>
                    <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 1 }}>
                      <IconButton
                        component={NavLink}
                        to="list-your-property"
                        size="small"
                      >
                        <HouseOutlinedIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={openSignInDialog}
                      >
                        <LoginIcon />
                      </IconButton>
                    </Box>
                  </React.Fragment>
                )}
              </Box>
          </Toolbar>
        </div>
      </AppBar>
    </React.Fragment>
  );
}

export default Header;

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 32px',
    '@media (min-width: 1240px)': {
      padding: 0,
    }
  },
  logo: {
    paddingTop: '5px',
    height: '35px',
    '@media (min-width: 600px)': {
      height: '45px',
    }
  },
  link: {
    padding: '0 8px',
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '-0.35px',
    color: 'rgba(0, 0, 0, 0.87)',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color 0.3s',
    '&:hover': {
      color: getContent('theme').primaryColor,
    }
  },
  activeLink: {
    color: getContent('theme').primaryColor,
  },
});
